import { Inertia } from '@inertiajs/inertia'
import { FunctionalComponent, h } from '@stencil/core'
import { JSXBase } from '@stencil/core/internal'
import { createStore } from '@stencil/store'
import Axios from 'axios'

import { HubspotLogo } from '../../customize/integrations/logos'
import { CallStore } from '../../page/call/detail/state'
import { useStoredValue } from '../../utils/storage'
import { ExternalLink } from '../../utils/url'
import { CrmTimeoutMS, getICrmStateDefaults, ICrmState, showNoContactsToast } from '../state'

export type HubspotIntegrationState = typeof HubspotIntegrationStore.state

declare global {
  interface LocalStorageKeys {
    hubspotConnectDismissed: boolean
  }
}

export const HubspotIntegrationStore = createStore<ICrmState>(getICrmStateDefaults())

HubspotIntegrationStore.use(
  useStoredValue(HubspotIntegrationStore, 'hubspotConnectDismissed', 'isConnectDismissed', false)
)

HubspotIntegrationStore.onChange('refreshingContacts', (newValue) => {
  showNoContactsToast(HubspotIntegrationStore, 'hubspot', newValue)
})

function onSyncTimeout() {
  if (HubspotIntegrationStore.state.callCurrentlySyncing) {
    HubspotIntegrationStore.state.syncTimedOut = true
    window.Sentry?.captureMessage(`Timed out syncing to HubSpot on call ${CallStore.state.call.id}`)
    Inertia.reload({ only: ['integrations'] })
  }
}

export async function syncToHubspot(isManualSync = false) {
  if (HubspotIntegrationStore.state.connectedTo) {
    HubspotIntegrationStore.state.callCurrentlySyncing = true
    HubspotIntegrationStore.state.timeout = window.setTimeout(() => onSyncTimeout(), CrmTimeoutMS)
    await Axios.post<string[]>(HubspotIntegrationStore.state.syncUrl, {
      is_manual_sync: isManualSync,
    })
  }
}

export async function toggleHubspotTaskSync(toggle: boolean) {
  await Axios.put(HubspotIntegrationStore.state.preferencesUrl, { auto_sync_action_items: toggle })
  HubspotIntegrationStore.state.taskSyncEnabled = toggle
}

export const ConnectHubspotBanner: FunctionalComponent<
  { connectUrl: string; message: string } & JSXBase.IntrinsicElements['a']
> = ({ connectUrl, message, class: className = '', ...attrs }, children) => (
  <ExternalLink
    href={connectUrl}
    class={`
      relative flex items-center p-2 pl-3 select-none transition duration-200 spacing-x-3 bg-info-warn text-info-warn bg-opacity-10 hover:bg-opacity-100 hover:text-surface-base
      ${className}
    `}
    {...attrs}
  >
    <HubspotLogo class='w-8' />
    <dl class='flex flex-col w-56'>
      <dt class='mb-1 text-lg font-extrabold'>Connect HubSpot</dt>
      <dd class='text-xs font-medium'>
        {message} <span class='font-extrabold'>Click to learn more</span>
      </dd>
    </dl>
    {children}
  </ExternalLink>
)
